import { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, TextField,
  CircularProgress, Chip, MenuItem, InputAdornment, Tooltip,
  Avatar, Stack, Grid, Pagination,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  History as AuditIcon, Search as SearchIcon, Clear as ClearIcon,
  Refresh as RefreshIcon, FilterAltOff as FilterOffIcon,
  Today as TodayIcon, DateRange as WeekIcon, Group as UsersIcon,
} from '@mui/icons-material';
import API from '../../BaseURL';

function actionPalette(action = '') {
  const a = action.toUpperCase();
  if (/CREATE|REGISTER|ADD|MAKE|RUN|IMPORT|GRANT|APPROVE|LOGIN|RESET|INITIALIZE/.test(a))
    return { bg: '#2e7d32', label: '#2e7d32' };
  if (/UPDATE|EDIT|SAVE|ASSIGN|CONFIRM|COPY|PAGES/.test(a))
    return { bg: '#1565c0', label: '#0d47a1' };
  if (/DELETE|DEACTIVATE|REMOVE|REJECT|CANCEL|REVOKE|LOGOUT/.test(a))
    return { bg: '#c62828', label: '#b71c1c' };
  if (/REQUEST|ACTION|PAYMENT|BILL|STATUS/.test(a))
    return { bg: '#ed6c02', label: '#e65100' };
  return { bg: '#6a1b9a', label: '#4a148c' };
}

const PAGE_SIZE = 50;

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <Box sx={{
      p: 2, borderRadius: 3,
      background: `linear-gradient(135deg, ${alpha(color, 0.12)}, ${alpha(color, 0.04)})`,
      border: `1px solid ${alpha(color, 0.2)}`,
      display: 'flex', alignItems: 'center', gap: 1.5, height: '100%',
    }}>
      <Avatar variant="rounded" sx={{ width: 40, height: 40, bgcolor: alpha(color, 0.18), color }}>
        <Icon sx={{ fontSize: 22 }} />
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h5" fontWeight={800} sx={{ color, lineHeight: 1.05 }}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 11 }}>
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

function relativeTime(iso) {
  if (!iso) return '—';
  const d  = new Date(iso);
  const ms = Date.now() - d.getTime();
  const s  = Math.floor(ms / 1000);
  if (s < 5)    return 'just now';
  if (s < 60)   return `${s}s ago`;
  const m = Math.floor(s / 60); if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days === 1) return 'yesterday';
  if (days < 7)   return `${days}d ago`;
  return d.toLocaleDateString('en-GH', { day: '2-digit', month: 'short', year: '2-digit' });
}

export default function AuditPage() {
  const theme = useTheme();
  const p = theme.palette.primary.main;

  const [logs, setLogs]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [stats, setStats]     = useState({ totals: { all: 0, today: 0, week: 0, active_users: 0 }, byAction: [], byTarget: [], byUser: [] });
  const [loading, setLoading] = useState(true);

  const [search, setSearch]       = useState('');
  const [actionSel, setActionSel] = useState('');
  const [targetSel, setTargetSel] = useState('');
  const [from, setFrom]           = useState('');
  const [to, setTo]               = useState('');
  const [page, setPage]           = useState(1);

  const loadStats = useCallback(async () => {
    try {
      const res = await API.get('/audit/stats');
      setStats(res.data || stats);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE };
      if (search.trim())  params.q           = search.trim();
      if (actionSel)      params.action      = actionSel;
      if (targetSel)      params.target_type = targetSel;
      if (from)           params.from        = from;
      if (to)             params.to          = to;

      const res = await API.get('/audit', { params });
      setLogs(res.data.results || []);
      setTotal(res.data.total || 0);
    } catch {
      setLogs([]); setTotal(0);
    } finally { setLoading(false); }
  }, [page, search, actionSel, targetSel, from, to]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const clearFilters = () => {
    setSearch(''); setActionSel(''); setTargetSel(''); setFrom(''); setTo(''); setPage(1);
  };
  const anyFilter = search || actionSel || targetSel || from || to;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Avatar variant="rounded" sx={{ width: 44, height: 44, bgcolor: alpha(p, 0.12), color: p }}>
          <AuditIcon />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="h6" fontWeight={800}>Audit Logs</Typography>
          <Typography variant="caption" color="text.secondary">
            Full trail of system actions — every create, update, delete and authentication event is captured here.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={() => { load(); loadStats(); }} disabled={loading}
              sx={{ border: '1px solid', borderColor: 'divider', bgcolor: '#fff', borderRadius: 1.6 }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Stat strip */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <StatCard label="Total Events" value={stats.totals?.all || 0} color={p} icon={AuditIcon} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard label="Today" value={stats.totals?.today || 0} color="#2e7d32" icon={TodayIcon} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard label="Last 7 Days" value={stats.totals?.week || 0} color="#1565c0" icon={WeekIcon} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard label="Active Users" value={stats.totals?.active_users || 0} color="#6a1b9a" icon={UsersIcon} />
        </Grid>
      </Grid>

      {/* Main card */}
      <Card variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
        {/* Toolbar */}
        <Box sx={{
          px: 2, py: 1.4,
          display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap',
          borderBottom: '1px solid', borderColor: 'divider',
          background: `linear-gradient(180deg, ${alpha(p, 0.04)} 0%, transparent 100%)`,
        }}>
          <TextField size="small" placeholder="Search action, description, user…"
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            sx={{ minWidth: 280, '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 2 } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment>,
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => { setSearch(''); setPage(1); }}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField select size="small" label="Action"
            value={actionSel} onChange={e => { setActionSel(e.target.value); setPage(1); }}
            sx={{ minWidth: 200, '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 2 } }}>
            <MenuItem value="">All actions</MenuItem>
            {(stats.byAction || []).slice(0, 30).map(a => (
              <MenuItem key={a.action} value={a.action}>
                {a.action} <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'text.secondary' }}>· {a.n}</Typography>
              </MenuItem>
            ))}
          </TextField>

          <TextField select size="small" label="Target"
            value={targetSel} onChange={e => { setTargetSel(e.target.value); setPage(1); }}
            sx={{ minWidth: 160, '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 2 } }}>
            <MenuItem value="">All targets</MenuItem>
            {(stats.byTarget || []).slice(0, 30).map(t => (
              <MenuItem key={t.target_type} value={t.target_type}>{t.target_type} · {t.n}</MenuItem>
            ))}
          </TextField>

          <TextField size="small" type="date" label="From"
            InputLabelProps={{ shrink: true }} value={from}
            onChange={e => { setFrom(e.target.value); setPage(1); }}
            sx={{ minWidth: 140, '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 2 } }}
          />
          <TextField size="small" type="date" label="To"
            InputLabelProps={{ shrink: true }} value={to}
            onChange={e => { setTo(e.target.value); setPage(1); }}
            sx={{ minWidth: 140, '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 2 } }}
          />

          {anyFilter && (
            <Tooltip title="Clear all filters">
              <IconButton size="small" onClick={clearFilters}
                sx={{ border: '1px solid', borderColor: 'divider', bgcolor: '#fff' }}>
                <FilterOffIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Table */}
        {loading ? (
          <Box sx={{ py: 7, textAlign: 'center' }}><CircularProgress size={28} /></Box>
        ) : logs.length === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Avatar sx={{ width: 56, height: 56, mx: 'auto', mb: 1.5, bgcolor: alpha(p, 0.08) }}>
              <AuditIcon sx={{ color: p, fontSize: 28 }} />
            </Avatar>
            <Typography variant="subtitle1" fontWeight={700}>
              {anyFilter ? 'No events match the filters' : 'No audit events yet'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {anyFilter
                ? 'Try a different date range or clear the filters.'
                : 'Once users start performing actions, they will appear here in real time.'}
            </Typography>
            {anyFilter && (
              <Button size="small" startIcon={<FilterOffIcon />} onClick={clearFilters}>
                Clear filters
              </Button>
            )}
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{
                  '& th': {
                    bgcolor: alpha(p, 0.04),
                    color: 'text.secondary',
                    fontWeight: 800, fontSize: 10.5,
                    letterSpacing: 0.6, textTransform: 'uppercase',
                    borderBottom: `2px solid ${alpha(p, 0.18)}`,
                    py: 1.1, whiteSpace: 'nowrap',
                  },
                }}>
                  <TableCell width={56} />
                  <TableCell>User</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell width={140}>When</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map(log => {
                  const initials = (log.full_name || log.username || '?').split(/\s+/)
                    .filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase()).join('') || '?';
                  const palette  = actionPalette(log.action);
                  return (
                    <TableRow key={log.id} hover sx={{
                      transition: 'background 0.15s',
                      '&:hover': { bgcolor: alpha(p, 0.03) },
                      '&:last-of-type td': { borderBottom: 0 },
                    }}>
                      <TableCell>
                        <Avatar sx={{
                          width: 34, height: 34, fontSize: 11.5, fontWeight: 800,
                          bgcolor: alpha(p, 0.1), color: p,
                          border: `1px solid ${alpha(p, 0.18)}`,
                        }}>{initials}</Avatar>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                          {log.full_name?.trim() || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 11 }}>
                          @{log.username || 'system'}
                          {log.department_name && <> · {log.department_name}</>}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={log.action || '—'}
                          sx={{
                            fontWeight: 700, fontSize: 10, height: 22,
                            bgcolor: alpha(palette.bg, 0.12),
                            color: palette.label,
                            border: `1px solid ${alpha(palette.bg, 0.3)}`,
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                          }} />
                      </TableCell>
                      <TableCell>
                        {log.target_type ? (
                          <>
                            <Typography variant="caption" sx={{
                              display: 'block', fontWeight: 700, fontFamily: 'ui-monospace, monospace',
                              fontSize: 11,
                            }}>
                              {log.target_type}
                            </Typography>
                            {log.target_id !== 0 && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, fontFamily: 'ui-monospace, monospace' }}>
                                #{log.target_id}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Tooltip title={log.description || ''}>
                          <Typography variant="caption" color="text.secondary" sx={{
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}>
                            {log.description || '—'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={log.created_at ? new Date(log.created_at).toLocaleString('en-GH') : ''}>
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11, display: 'block' }}>
                              {relativeTime(log.created_at)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                              {log.created_at ? new Date(log.created_at).toLocaleTimeString('en-GH', { hour: '2-digit', minute: '2-digit' }) : ''}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Footer / Pagination */}
        {!loading && logs.length > 0 && (
          <Box sx={{
            px: 2, py: 1,
            borderTop: '1px solid', borderColor: 'divider',
            bgcolor: alpha(p, 0.02),
            display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
          }}>
            <Typography variant="caption" color="text.secondary">
              Showing <strong>{(page - 1) * PAGE_SIZE + 1}</strong>–
              <strong>{Math.min(page * PAGE_SIZE, total)}</strong> of <strong>{total}</strong>
            </Typography>
            <Box sx={{ flex: 1 }} />
            {totalPages > 1 && (
              <Pagination
                size="small" count={totalPages} page={page}
                onChange={(_, v) => setPage(v)}
                color="primary" shape="rounded" siblingCount={1}
              />
            )}
          </Box>
        )}
      </Card>

      {/* Most-active sub-strip */}
      {(stats.byUser || []).length > 0 && (
        <Card variant="outlined" sx={{ mt: 2, borderRadius: 3 }}>
          <Box sx={{ px: 2, py: 1.4, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar sx={{ width: 24, height: 24, bgcolor: alpha('#6a1b9a', 0.12) }}>
                <UsersIcon sx={{ color: '#6a1b9a', fontSize: 14 }} />
              </Avatar>
              <Typography variant="subtitle2" fontWeight={800}>Most active users</Typography>
            </Stack>
          </Box>
          <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {stats.byUser.map(u => {
              const initials = (u.full_name || u.username || '?').split(/\s+/)
                .filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase()).join('') || '?';
              return (
                <Box key={u.id || 'anon'} sx={{
                  display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 2,
                  bgcolor: alpha(p, 0.04), border: `1px solid ${alpha(p, 0.12)}`,
                  minWidth: 180,
                }}>
                  <Avatar sx={{
                    width: 28, height: 28, fontSize: 10, fontWeight: 800,
                    bgcolor: alpha(p, 0.1), color: p,
                    border: `1px solid ${alpha(p, 0.18)}`,
                  }}>{initials}</Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="caption" fontWeight={700} sx={{ display: 'block', lineHeight: 1.1 }}>
                      {u.full_name?.trim() || '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                      @{u.username || 'system'}
                    </Typography>
                  </Box>
                  <Chip size="small" label={u.n}
                    sx={{ height: 18, fontSize: 10, fontWeight: 800,
                          bgcolor: alpha(p, 0.12), color: p }} />
                </Box>
              );
            })}
          </Box>
        </Card>
      )}
    </Box>
  );
}
